import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { SkipLink } from "@/components/_atoms/skip-link";
import { useNavigate } from "@/components/_molecules/navigation-controls/use-navigate";
import { currentUserQueryDocument } from "@/domain/user/hooks/api/user.gql";
import { WorkspaceSelect } from "@/domain/workspace/components/workspace-select";
import { isWorkspacePickerRequested } from "@/domain/workspace/lib/is-workspace-picker-requested";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { getWorkspaceSettings, isWorkspaceSettingType, setWorkspaceSettings } from "@/services/workspace.service";

export const AppLayout = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    selectInitialWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function selectInitialWorkspace() {
    // Reached from the SSO-block screen, whose only workspace is enforced: auto-entering it
    // would bounce straight back to the block, so the picker is shown instead of a redirect.
    if (isWorkspacePickerRequested(searchParams)) {
      setInitialized(true);
      return;
    }

    const localWorkspaceSettings = getWorkspaceSettings();

    if (!localWorkspaceSettings) {
      const workspaceMembershipList = await getActiveWorkspacesFromApi();

      if (
        workspaceMembershipList instanceof Array &&
        workspaceMembershipList.length === 1 &&
        workspaceMembershipList[0].workspace &&
        isWorkspaceSettingType(workspaceMembershipList[0].workspace)
      ) {
        setWorkspaceSettings(workspaceMembershipList[0].workspace);
        navigate(`/${workspaceMembershipList[0].workspace.workspaceUrl}`, {
          replace: true,
        });
      }
      setInitialized(true);
    } else {
      setInitialized(true);
      navigate(`/${localWorkspaceSettings.workspaceUrl}`, {
        replace: true,
      });
    }
  }

  async function getActiveWorkspacesFromApi() {
    const client = createOperationClient("currentUser");
    const data = await client.request(currentUserQueryDocument);
    if (!data.currentUser.workspaceMembershipList) {
      return [];
    }

    const workspaceMembershipList = data.currentUser.workspaceMembershipList;
    const activeWorkspaceMembershipList = workspaceMembershipList.filter((e) => e.status === "ACTIVE");

    return activeWorkspaceMembershipList;
  }

  if (!initialized) {
    return (
      <main
        className="align-center relative flex h-screen select-none justify-center overflow-auto"
        aria-label={t("a11y.loadingApplication")}
      >
        <div className="sr-only">{t("a11y.loadingApplication")}</div>
      </main>
    );
  }

  return (
    <>
      <SkipLink targetId="main-content" />
      <main id="main-content" tabIndex={-1} aria-label={t("a11y.mainContent")} className="outline-hidden">
        <WorkspaceSelect />
      </main>
    </>
  );
};
